import { useState } from 'react';
import { Dialog, Skeleton, Toggle } from '../../../../ds/components';
import { clsx } from '../../../../ds/clsx';
import { errorMessage } from '../../../../lib/api';
import { WizardFooter, WizardIntro } from '../../ui';
import { LoadError } from '../LoadError';
import { sendLabel, useTeamInvites } from '../teamInvites';
import type { InvitationPreview } from '../types';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import styles from './steps.module.css';

/** Onboarding Admin 10 · Team: invitation rows (e-mail, role, own billing), seats, invitation text preview. */
export function TeamStep() {
  const nav = useWizardNav();
  const team = useTeamInvites();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pending, setPending] = useState<'alone' | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);

  async function send() {
    setStepError(null);
    const allSent = await team.send();
    if (!allSent) return;
    try {
      await nav.complete('team', 'continue', 'security');
    } catch (error) {
      setStepError(errorMessage(error));
    }
  }

  async function alone() {
    setPending('alone');
    setStepError(null);
    try {
      await nav.complete('team', 'skip', 'security');
    } catch (error) {
      setStepError(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  const roleOptions = team.roles.data?.items.map((role) => ({ value: role.id, label: role.name })) ?? [];
  return (
    <>
      <WizardIntro
        eyebrow={stepEyebrow('team')}
        title="Wer arbeitet mit?"
        textWidth={640}
        text="Jede Einladung ist sieben Tage gültig. Steht „eigene Abrechnung“ auf ja, hinterlegt der Nutzer im eigenen Onboarding ein Zahlungsmittel."
      />
      {team.roles.isPending ? (
        <Skeleton variant="block" height={260} />
      ) : team.roles.isError ? (
        <LoadError
          what="Die Rollen konnten nicht geladen werden"
          error={team.roles.error}
          retrying={team.roles.isFetching}
          onRetry={() => void team.roles.refetch()}
        />
      ) : (
        <div className={styles.teamTable} role="table" aria-label="Einladungen">
          <div className={clsx(styles.teamRow, styles.teamHead)} role="row">
            <span role="columnheader">E-Mail-Adresse</span>
            <span role="columnheader">Rolle</span>
            <span role="columnheader">Eigene Abrechnung</span>
            <span role="columnheader" />
          </div>
          {team.rows.map((row, index) => {
            const state = team.states[index] ?? { error: null, hint: null, valid: false };
            const position = index + 1;
            return (
              <div key={row.id} className={styles.teamRow} role="row">
                <div className={styles.teamCell} role="cell">
                  <div className={clsx(styles.compactControl, state.error && styles.compactInvalid)}>
                    <input
                      className={styles.compactInput}
                      type="email"
                      value={row.email}
                      aria-label={`E-Mail-Adresse ${position}`}
                      aria-invalid={state.error ? true : undefined}
                      placeholder="name@firma.de"
                      onChange={(event) => team.update(row.id, { email: event.target.value })}
                    />
                  </div>
                  {state.error ? (
                    <span className={styles.rowMessage} role="alert">
                      {state.error}
                    </span>
                  ) : state.hint ? (
                    <span className={clsx(styles.rowMessage, styles.rowHint)}>{state.hint}</span>
                  ) : null}
                </div>
                <div className={styles.teamCell} role="cell">
                  <div className={clsx(styles.compactControl, styles.compactSelect)}>
                    <select
                      aria-label={`Rolle ${position}`}
                      value={team.roleOf(row)}
                      onChange={(event) => team.update(row.id, { roleId: event.target.value })}
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className={styles.compactChevron} aria-hidden="true">
                      ⌄
                    </span>
                  </div>
                </div>
                <div className={styles.billingCell} role="cell">
                  <Toggle
                    size="large"
                    checked={row.ownBilling}
                    onChange={(checked) => team.update(row.id, { ownBilling: checked })}
                    label={`Eigene Abrechnung ${position}`}
                    labelHidden
                  />
                  <span
                    className={clsx(styles.billingLabel, row.ownBilling && styles.billingOn)}
                    aria-hidden="true"
                  >
                    {row.ownBilling ? 'selbst zahlend' : 'über Firma'}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.removeRow}
                  aria-label={`Zeile ${position} entfernen`}
                  onClick={() => team.remove(row.id)}
                >
                  Zeile entfernen
                </button>
              </div>
            );
          })}
          <div className={styles.addRow}>
            <button type="button" className={clsx('pressable', styles.addButton)} onClick={team.add}>
              + Weitere Person
            </button>
          </div>
        </div>
      )}
      <div className={styles.infoCards}>
        <section className={styles.infoCard} aria-label="Plätze im Tarif">
          <span className={styles.infoTitle}>Plätze im Tarif</span>
          <span className={styles.infoText}>{team.seatLabel ?? '—'}</span>
        </section>
        <section className={styles.infoCard} aria-label="Einladungstext">
          <span className={styles.infoTitle}>Einladungstext</span>
          <span className={clsx(styles.infoText, styles.infoMuted)}>
            Standardtext mit Ihrem Logo ·{' '}
            <button type="button" className={styles.linkButton} onClick={() => setPreviewOpen(true)}>
              Vorschau ansehen
            </button>
          </span>
        </section>
      </div>
      {team.failure || stepError ? (
        <div className={styles.error} role="alert">
          {stepError ?? team.failure}
        </div>
      ) : null}
      <WizardFooter
        back={{ label: 'Zurück', onClick: () => nav.go('telephony') }}
        secondary={{ label: 'Allein starten', loading: pending === 'alone', onClick: () => void alone() }}
        primary={{
          label: sendLabel(team.count),
          disabled: team.count === 0 || team.blocked,
          loading: team.sending,
          onClick: () => void send(),
        }}
      />
      <InvitationPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={team.preview.data}
        error={team.preview.error}
      />
    </>
  );
}

export function InvitationPreviewDialog({
  open,
  onClose,
  preview,
  error,
}: {
  open: boolean;
  onClose: () => void;
  preview: InvitationPreview | undefined;
  error: unknown;
}) {
  return (
    <Dialog open={open} onClose={onClose} eyebrow="Einladungstext" title="Vorschau der Einladung" width={600}>
      {preview ? (
        <>
          <span className={styles.previewSubject}>{preview.subject}</span>
          <p className={styles.previewText}>{preview.text}</p>
        </>
      ) : error ? (
        <span className={styles.error} role="alert">
          {errorMessage(error)}
        </span>
      ) : (
        <Skeleton variant="block" height={200} />
      )}
    </Dialog>
  );
}
